/**
 * Analytics Injectors (P20250925-098)
 *
 * Strictly env-gated script injectors for Clarity and SalesIQ.
 * ONLY load in production with explicit env flags.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { track, ANALYTICS_FLAGS } from './index';
import { getConsent, onConsentChange } from '../privacy/consent';

/**
 * Microsoft Clarity - Prod-only, env-gated, consent-required
 * B20251011-Stack-Switch-Phase01: Exclude /manage/* routes
 */
export function ClarityInjector() {
  const location = useLocation();
  const clarityId = import.meta.env.VITE_CLARITY_ID || import.meta.env.NEXT_PUBLIC_CLARITY_ID;

  useEffect(() => {
    // Phase 01: Block all /manage routes explicitly
    const isAdminRoute = location.pathname.startsWith('/manage');
    if (isAdminRoute) {
      if (import.meta.env.DEV) {
        console.info('[Clarity] Blocked on /manage route');
      }
      return;
    }

    // Guard: only load if flag enabled AND in production AND consent granted
    const consent = getConsent();
    const hasAnalyticsConsent = consent?.analytics === true;

    if (!ANALYTICS_FLAGS.CLARITY_ENABLED || !clarityId || !hasAnalyticsConsent) {
      if (import.meta.env.DEV) {
        console.info('[Clarity] Disabled:', {
          enabled: ANALYTICS_FLAGS.CLARITY_ENABLED,
          hasId: !!clarityId,
          isProd: import.meta.env.PROD,
          consent: hasAnalyticsConsent,
        });
      }
      return;
    }

    // Additional guards
    const dnt = navigator.doNotTrack === '1' || (navigator as any).msDoNotTrack === '1';
    const isAttainDomain = window.location.host.endsWith('attaindomains.com');

    if (dnt || !isAttainDomain) return;

    // Check if already loaded
    if ((window as any).clarity) return;

    // Inject Clarity
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${clarityId}`;

    // Initialize clarity function
    (window as any).clarity =
      (window as any).clarity ||
      function (...args: any[]) {
        ((window as any).clarity.q = (window as any).clarity.q || []).push(args);
      };

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode!.insertBefore(script, firstScript);

    // Track load
    track('clarity_loaded', { page: location.pathname });

    if (import.meta.env.DEV) {
      console.info('[Clarity] Loaded');
    }
  }, [location.pathname, clarityId]);

  return null;
}

/**
 * Zoho SalesIQ - Prod-only, env-gated, consent-required
 * B20251011-Stack-Switch-Phase01: Exclude /manage/* routes
 */
export function SalesIQInjector() {
  const location = useLocation();
  const widgetId = import.meta.env.VITE_SALESIQ_WIDGET_ID || import.meta.env.NEXT_PUBLIC_SALESIQ_ID;
  const brand = import.meta.env.VITE_SALESIQ_BRAND || 'Attain Domains';

  useEffect(() => {
    // Phase 01: Block all /manage routes explicitly
    const isAdminRoute = location.pathname.startsWith('/manage');
    if (isAdminRoute) {
      if (import.meta.env.DEV) {
        console.info('[SalesIQ] Blocked on /manage route');
      }
      return;
    }

    // Guard: only load if flag enabled AND in production AND consent granted
    const consent = getConsent();
    const hasAnalyticsConsent = consent?.analytics === true;

    if (!ANALYTICS_FLAGS.SALESIQ_ENABLED || !widgetId || !hasAnalyticsConsent) {
      if (import.meta.env.DEV) {
        console.info('[SalesIQ] Disabled:', {
          enabled: ANALYTICS_FLAGS.SALESIQ_ENABLED,
          hasId: !!widgetId,
          isProd: import.meta.env.PROD,
          consent: hasAnalyticsConsent,
        });
      }
      return;
    }

    // Don't load on non-AttainDomains hosts
    const isAttainDomain = window.location.host.endsWith('attaindomains.com');
    if (!isAttainDomain) return;

    // Check if already loaded
    if (document.getElementById('zsiqscript')) return;

    // Initialize $zoho
    if (!window.$zoho) window.$zoho = {};
    if (!window.$zoho.salesiq) {
      window.$zoho.salesiq = {
        ready: () => {},
      };
    }

    // Inject SalesIQ
    const script = document.createElement('script');
    script.id = 'zsiqscript';
    script.src = `https://salesiq.zohopublic.com/widget?wc=${widgetId}`;
    script.defer = true;
    script.async = true;

    // Track ready
    const originalReady = window.$zoho.salesiq?.ready;
    if (window.$zoho.salesiq) {
      window.$zoho.salesiq.ready = function (callback: () => void) {
        track('salesiq_ready', { brand, page: location.pathname });

        if (originalReady) {
          originalReady(callback);
        } else if (callback) {
          callback();
        }
      };
    }

    document.body.appendChild(script);

    if (import.meta.env.DEV) {
      console.info('[SalesIQ] Loaded');
    }

    // Cleanup
    return () => {
      const scriptToRemove = document.getElementById('zsiqscript');
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [location.pathname, widgetId, brand]);

  return null;
}

declare global {
  interface Window {
    $zoho?: {
      salesiq?: {
        ready: (callback: () => void) => void;
        chat?: {
          start?: () => void;
          close?: () => void;
        };
      };
    };
  }
}
